import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, DownloadSimple } from "@phosphor-icons/react";
import { fetchHistory, historyToCsv } from "@/lib/history";
import { LOCALES, useI18n } from "@/lib/i18n";

const PAGE = 50;
const STATUSES = ["done", "error", "expired", "cancelled"];
const STATUS_CLASS = {
    done: "bg-green-50 border-green-300 text-green-900",
    error: "bg-red-50 border-red-300 text-red-800",
    expired: "bg-brand-100 border-brand-300 text-brand-800",
    cancelled: "bg-brand-100 border-brand-300 text-brand-800",
    queued: "bg-amber-50 border-amber-300 text-amber-900",
    sent: "bg-amber-50 border-amber-300 text-amber-900",
};

const ymd = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function rangeFor(preset) {
    const today = new Date();
    if (preset === "today") return { from: ymd(today), to: ymd(today) };
    if (preset === "7d") {
        const start = new Date(today);
        start.setDate(start.getDate() - 6);
        return { from: ymd(start), to: ymd(today) };
    }
    if (preset === "month") return { from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: ymd(today) };
    return { from: "", to: "" };
}

const field = "bg-white border border-brand-300 text-brand-950 text-sm rounded-none px-2.5 py-1.5 focus:outline-none focus:border-brand-900";
const labelCls = "text-[10px] font-semibold text-brand-800 uppercase tracking-wider mb-1 block";

/** Historial de impresión: fecha, plantilla, etiquetas, hojas, impresora y equipo, con filtros y exportación. */
export default function HistoryModal({ open, onClose }) {
    const { t, lang } = useI18n();
    const [preset, setPreset] = useState("7d");
    const [range, setRange] = useState(() => rangeFor("7d"));
    const [printer, setPrinter] = useState("");
    const [status, setStatus] = useState("");
    const [q, setQ] = useState("");
    const [debouncedQ, setDebouncedQ] = useState("");
    const [data, setData] = useState({ items: [], total: 0, printed: { labels: 0, pages: 0, jobs: 0 }, printers: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const latest = useRef(0);
    const locale = LOCALES[lang];
    const num = (n) => (n ?? 0).toLocaleString(locale);

    useEffect(() => {
        const id = setTimeout(() => setDebouncedQ(q.trim()), 300);
        return () => clearTimeout(id);
    }, [q]);

    const load = useCallback(
        async (offset = 0) => {
            const mine = ++latest.current;
            setLoading(true);
            setError("");
            try {
                const res = await fetchHistory({ from: range.from, to: range.to, printer, status, q: debouncedQ, limit: PAGE, offset });
                if (mine !== latest.current) return; // una consulta más nueva ya salió: esta se descarta
                setData((prev) => (offset ? { ...res, items: [...prev.items, ...res.items] } : res));
            } catch {
                if (mine === latest.current) setError(t("history.loadError"));
            } finally {
                if (mine === latest.current) setLoading(false);
            }
        },
        [range, printer, status, debouncedQ, t]
    );

    useEffect(() => {
        if (open) load(0);
    }, [open, load]);

    function choosePreset(next) {
        setPreset(next);
        setRange(rangeFor(next));
    }

    async function exportCsv() {
        const all = [];
        while (all.length < 5000) {
            const res = await fetchHistory({ from: range.from, to: range.to, printer, status, q: debouncedQ, limit: 200, offset: all.length });
            all.push(...res.items);
            if (!res.items.length || all.length >= res.total) break;
        }
        const headers = ["date", "template", "labels", "pages", "printer", "computer", "status"].map((k) =>
            k === "date" ? t("history.col.date") : t(`history.col.${k}`)
        );
        const csv = historyToCsv(all, headers, (s) => t(`history.status.${s}`));
        const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `historial_${ymd(new Date())}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    if (!open) return null;

    const summary = t("history.summary", {
        jobs: t("history.jobs", { n: data.printed.jobs }),
        labels: t("history.labels", { n: data.printed.labels }),
        pages: t("history.pages", { n: data.printed.pages }),
    });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-950/50 p-6" translate="no">
            <div className="bg-white w-full max-w-5xl max-h-[90vh] flex flex-col shadow-2xl border border-brand-300">
                <div className="px-5 py-3.5 border-b border-brand-200 bg-brand-50/60 flex items-center justify-between">
                    <h2 className="font-headings text-lg font-semibold text-brand-900">{t("history.title")}</h2>
                    <button data-testid="history-close" onClick={onClose} className="p-1.5 text-brand-700 hover:bg-brand-100" aria-label={t("common.close")}>
                        <X size={18} />
                    </button>
                </div>

                <div className="px-5 py-3 border-b border-brand-200 flex flex-wrap items-end gap-3">
                    <div className="flex items-center gap-1" role="group">
                        {["today", "7d", "month", "all"].map((p) => (
                            <button
                                key={p}
                                type="button"
                                data-testid={`history-preset-${p}`}
                                onClick={() => choosePreset(p)}
                                className={`px-2.5 py-1.5 text-xs font-medium border ${
                                    preset === p ? "bg-brand-900 text-white border-brand-900" : "bg-white text-brand-900 border-brand-300 hover:bg-brand-100"
                                }`}
                            >
                                {t(`history.range.${p}`)}
                            </button>
                        ))}
                    </div>
                    <div>
                        <label className={labelCls} htmlFor="history-from">{t("history.from")}</label>
                        <input id="history-from" data-testid="history-from" type="date" value={range.from}
                            onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, from: e.target.value })); }} className={field} />
                    </div>
                    <div>
                        <label className={labelCls} htmlFor="history-to">{t("history.to")}</label>
                        <input id="history-to" data-testid="history-to" type="date" value={range.to}
                            onChange={(e) => { setPreset("custom"); setRange((r) => ({ ...r, to: e.target.value })); }} className={field} />
                    </div>
                    <div>
                        <label className={labelCls} htmlFor="history-printer">{t("history.printer")}</label>
                        <select id="history-printer" data-testid="history-printer" value={printer} onChange={(e) => setPrinter(e.target.value)} className={`${field} max-w-[14rem]`}>
                            <option value="">{t("history.allPrinters")}</option>
                            {data.printers.map((p) => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={labelCls} htmlFor="history-status">{t("history.status")}</label>
                        <select id="history-status" data-testid="history-status" value={status} onChange={(e) => setStatus(e.target.value)} className={field}>
                            <option value="">{t("history.allStatus")}</option>
                            {STATUSES.map((s) => (
                                <option key={s} value={s}>{t(`history.status.${s}`)}</option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1 min-w-[10rem]">
                        <label className={labelCls} htmlFor="history-q">{t("history.col.template")}</label>
                        <input id="history-q" data-testid="history-q" type="search" value={q} onChange={(e) => setQ(e.target.value)}
                            placeholder={t("history.search")} className={`${field} w-full`} />
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-brand-100 text-brand-900 text-left text-xs uppercase tracking-wider sticky top-0">
                            <tr>
                                <th className="px-3 py-2">{t("history.col.date")}</th>
                                <th className="px-3 py-2">{t("history.col.template")}</th>
                                <th className="px-3 py-2 text-right">{t("history.col.labels")}</th>
                                <th className="px-3 py-2 text-right">{t("history.col.pages")}</th>
                                <th className="px-3 py-2">{t("history.col.printer")}</th>
                                <th className="px-3 py-2">{t("history.col.computer")}</th>
                                <th className="px-3 py-2">{t("history.col.status")}</th>
                            </tr>
                        </thead>
                        <tbody data-testid="history-rows">
                            {data.items.map((j) => (
                                <tr key={j.id} className="border-t border-brand-200 hover:bg-brand-50/60" data-testid="history-row">
                                    <td className="px-3 py-1.5 font-mono text-xs whitespace-nowrap">
                                        {new Date(j.createdAt).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" })}
                                    </td>
                                    <td className="px-3 py-1.5">{j.title || "—"}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{j.labels === null || j.labels === undefined ? "—" : num(j.labels)}</td>
                                    <td className="px-3 py-1.5 text-right font-mono">{j.pages === null || j.pages === undefined ? "—" : num(j.pages)}</td>
                                    <td className="px-3 py-1.5 font-mono text-xs">{j.printer || "—"}</td>
                                    <td className="px-3 py-1.5">{j.agentName || "—"}</td>
                                    <td className="px-3 py-1.5">
                                        <span title={j.error || ""} className={`inline-block px-2 py-0.5 text-[11px] border ${STATUS_CLASS[j.status] || STATUS_CLASS.expired}`}>
                                            {t(`history.status.${j.status}`)}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!loading && !error && data.items.length === 0 && (
                        <p data-testid="history-empty" className="p-8 text-center text-sm text-brand-700">{t("history.empty")}</p>
                    )}
                    {loading && <p className="p-4 text-center text-sm text-brand-700 font-mono">{t("history.loading")}</p>}
                    {error && <p data-testid="history-error" className="p-4 text-center text-sm text-red-800">{error}</p>}
                    {!loading && data.items.length < data.total && (
                        <div className="p-3 text-center">
                            <button data-testid="history-more" onClick={() => load(data.items.length)}
                                className="px-4 py-1.5 text-sm bg-white border border-brand-300 text-brand-900 hover:bg-brand-100 font-medium">
                                {t("history.more")}
                            </button>
                        </div>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-brand-200 bg-brand-50/60 flex items-center justify-between gap-3">
                    <div data-testid="history-summary" className="text-sm font-mono text-brand-900">{summary}</div>
                    <button data-testid="history-export" onClick={exportCsv} disabled={data.total === 0}
                        className="px-3 py-1.5 text-sm bg-white border border-brand-300 text-brand-900 hover:bg-brand-100 font-medium flex items-center gap-2 disabled:opacity-50">
                        <DownloadSimple size={15} />
                        <span>{t("history.export")}</span>
                    </button>
                </div>
            </div>
        </div>
    );
}
